import React, { Fragment, useEffect, useState } from 'react';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import {
	FaceFrownIcon,
	MagnifyingGlassIcon,
	ChevronRightIcon,
	ChevronLeftIcon,
} from '@heroicons/react/24/outline';

import { classNames, createDataTree } from '../components/helpers/functions';
import useModal from '../hooks/useModal';
import Button from '../components/Button';

const MODAL_NAME = 'selectLocation';

let findDeep = (data, value, key = 'id') => {
	let found = null;

	const find = (d, v) =>
		d.find((o) => {
			if (o[key] === v && !found) {
				found = o;
			} else if (o.children) {
				find(o.children, v);
			}
		});

	find(data, value);
	return found;
};

const SelectLocationModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const [query, setQuery] = useState('');
	const [selectedLocation, setSelectedLocation] = useState(null);
	const { setOpen: openLocationModal } = useModal('addLocation');

	const locations = open?.locations?.rows?.filter(
		(location) => !open?.skip?.includes(location.type),
	);
	const selected = open?.selected;

	const locationTree = createDataTree(locations);

	useEffect(() => {
		if (selected) {
			setSelectedLocation(findDeep(locationTree, selected));
		}
	}, [selected]);

	const onClose = () => {
		setClose();
		setSelectedLocation(null);
	};

	const onSelectLocation = (location) => {
		if (location?.children?.length) {
			setSelectedLocation(location);
		} else if (!location) {
			setSelectedLocation(null);
		} else {
			open?.onSelect(location);
			onClose();
		}
	};

	const LocationItem = ({ location }) => (
		<a
			key={location.id}
			className={classNames(
				location.current
					? 'bg-gray-100 text-gray-900'
					: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
				'group flex items-center px-6 py-4 text-sm font-medium rounded-md cursor-pointer justify-between',
			)}
			onClick={() => onSelectLocation(location)}
		>
			<span className="truncate">{location.name}</span>
			{location?.children?.length ? (
				<ChevronRightIcon className="h-5 w-5 text-gray-400" />
			) : null}
		</a>
	);

	const showLocations = selectedLocation
		? selectedLocation?.children
		: locationTree;

	const searchResults =
		query !== ''
			? locations?.filter((o) =>
					o?.name?.toLowerCase()?.includes(query?.toLowerCase()),
				)
			: [];

	return (
		<Transition.Root
			show={!!open}
			as={Fragment}
			afterLeave={() => setQuery('')}
			appear
		>
			<Dialog as="div" className="relative z-50" onClose={() => onClose()}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500/25 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<Dialog.Panel className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
							<Combobox>
								{!open?.disableSearch && (
									<div className="relative">
										<MagnifyingGlassIcon
											className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
											aria-hidden="true"
										/>
										<Combobox.Input
											className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 focus:outline-none sm:text-sm"
											placeholder="Search..."
											onChange={(event) => setQuery(event.target.value)}
										/>
									</div>
								)}

								{query === '' ? (
									<>
										<div className="border-b border-gray-200 bg-white px-4 py-5 sm:px-6">
											<div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
												<div className="ml-4 mt-2 flex items-center justify-between w-full">
													<h3 className="text-lg font-medium leading-6 text-gray-900 mr-3">
														{selectedLocation ? (
															<div
																className="flex items-center cursor-pointer"
																onClick={() =>
																	onSelectLocation(
																		findDeep(
																			locationTree,
																			selectedLocation?.parent_id,
																		),
																	)
																}
															>
																<ChevronLeftIcon className="h-5 w-5 text-gray-400 mr-3" />{' '}
																{selectedLocation?.name}
															</div>
														) : (
															'All Locations'
														)}
													</h3>
													{!open?.preventNew && !selectedLocation && (
														<Button
															onClick={() => {
																openLocationModal({
																	onSubmit: (newLocation) =>
																		onSelectLocation(newLocation),
																});
																onClose();
															}}
														>
															Create a Location
														</Button>
													)}
												</div>
												{selectedLocation && (
													<div className="ml-4 mt-6 sm:mt-2 flex-shrink-0">
														<button
															type="button"
															className={classNames(
																'relative inline-flex items-center rounded-md border border-transparent  px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
																selectedLocation.id === selected
																	? ' bg-gray-50 text-gray-400 pointer-events-none'
																	: ' bg-indigo-600 hover:bg-indigo-700 text-white',
															)}
															onClick={() => {
																open?.onSelect(selectedLocation);
																onClose();
															}}
														>
															{selectedLocation.id === selected
																? 'Selected'
																: 'Select'}
														</button>
													</div>
												)}
											</div>
										</div>
										<div className="space-y-1">
											{showLocations?.map((location) => (
												<LocationItem location={location} />
											))}
										</div>
									</>
								) : searchResults?.length ? (
									searchResults?.map((location) => (
										<LocationItem location={location} />
									))
								) : (
									<div className="border-t border-gray-100 py-14 px-6 text-center text-sm sm:px-14">
										<FaceFrownIcon
											className="mx-auto h-6 w-6 text-gray-400"
											aria-hidden="true"
										/>
										<p className="mt-4 font-semibold text-gray-900">
											No results found
										</p>
										<p className="mt-2 text-gray-500">
											We couldn’t find a location with that name. Please try
											again.
										</p>
									</div>
								)}
							</Combobox>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>
	);
};

export default SelectLocationModal;
